// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, InputRow, fmt } from './shared';

const TASSO_TIPICO = { pubblico: 7.5, privato: 10.5 };
const MAX_DURATA = 120; // 10 years in months

const CessioneQuinto = () => {
  const [stipendio, setStipendio] = useState('1800');
  const [tipo, setTipo] = useState('pubblico');
  const [importoRichiesto, setImportoRichiesto] = useState('15000');
  const [durataDesiderata, setDurataDesiderata] = useState('60');

  const S = parseFloat(stipendio) || 0;
  const quinto = S / 5;
  const tan = TASSO_TIPICO[tipo];
  const r = tan / 100 / 12;
  const D = Math.min(parseInt(durataDesiderata) || 60, MAX_DURATA);
  const I = parseFloat(importoRichiesto) || 0;

  // Calcola rata per l'importo richiesto
  const rataRichiesta = useMemo(() => {
    if (r === 0) return I / D;
    return I * (r * Math.pow(1 + r, D)) / (Math.pow(1 + r, D) - 1);
  }, [I, D, r]);

  // Importo massimo ottenibile con la rata = quinto stipendio
  const importoMassimo = useMemo(() => {
    if (r === 0) return quinto * D;
    return quinto * (Math.pow(1 + r, D) - 1) / (r * Math.pow(1 + r, D));
  }, [quinto, D, r]);

  const rataOk = rataRichiesta <= quinto;
  const totaleInteressi = rataRichiesta * D - I;
  const totalePagato = rataRichiesta * D;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="cessione-quinto-calc">
      <EducBox text="La cessione del quinto è un prestito trattenuto direttamente dallo stipendio o pensione INPS, in misura massima di un quinto (20%) del netto mensile. È riservata a dipendenti pubblici, privati e pensionati. Non serve garante e non c'è rischio di protestato." />
      <p className="text-xs text-[#888780] mt-3 mb-4 p-3 rounded-lg" data-testid="cessione-quinto-note"
        style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <strong className="text-[#C9A84C]">Nota TAN/TAEG.</strong> TAN indicativo — varia per profilo, importo e istituto. L'assicurazione vita e perdita impiego è obbligatoria per legge (D.P.R. 180/1950) e il suo costo è incluso nel TAEG — per questo TAN e TAEG differiscono significativamente.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Stipendio netto mensile (€)" value={stipendio} onChange={setStipendio} prefix="€" />
        <div>
          <label className="text-[#888780] text-sm block mb-1">Tipo di contratto</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
            <option value="pubblico">Dipendente pubblico / PA</option>
            <option value="privato">Dipendente privato</option>
          </select>
        </div>
        <InputRow label="Importo richiesto (€)" value={importoRichiesto} onChange={setImportoRichiesto} prefix="€" />
        <InputRow label="Durata desiderata (mesi)" value={durataDesiderata} onChange={setDurataDesiderata} suffix="mesi" min={24} max={120} />
      </div>

      {/* Quinto massimo */}
      <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-[#888780] mb-1">Stipendio netto mensile</p>
            <p className="text-xl font-bold text-[#E8E8E4]">€{fmt(S)}</p>
          </div>
          <div>
            <p className="text-xs text-[#888780] mb-1">Rata massima (1/5 stipendio)</p>
            <p className="text-xl font-bold text-[#C9A84C]">€{fmt(quinto)}</p>
          </div>
          <div>
            <p className="text-xs text-[#888780] mb-1">Importo massimo ottenibile</p>
            <p className="text-xl font-bold text-[#3D7A52]">€{fmt(importoMassimo)}</p>
          </div>
        </div>
      </div>

      {/* Verifica fattibilità */}
      <div className={`mb-6 p-4 rounded-xl text-sm ${rataOk ? 'bg-green-900/20 border border-green-700/40 text-green-300' : 'bg-red-900/20 border border-red-700/40 text-red-300'}`}
        data-testid="cessione-verifica">
        {rataOk
          ? `✓ Fattibile: rata €${fmt(rataRichiesta)}/mese ≤ quinto (€${fmt(quinto)}/mese)`
          : `✗ Non fattibile: rata €${fmt(rataRichiesta)}/mese > quinto consentito (€${fmt(quinto)}/mese). Riduci l'importo o aumenta la durata.`}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <ResultCard label="Rata mensile" value={`€${fmt(rataRichiesta)}`} gold />
        <ResultCard label="TAN tipico" value={`${tan}%`} sub={tipo === 'pubblico' ? 'Dipendenti pubblici' : 'Dipendenti privati'} />
        <ResultCard label="Totale interessi" value={`€${fmt(totaleInteressi)}`} />
        <ResultCard label="Totale restituito" value={`€${fmt(totalePagato)}`} />
      </div>

      {/* Note normativa */}
      <div className="p-4 rounded-xl space-y-2 text-sm text-[#888780]" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="font-medium text-[#E8E8E4] mb-2">Note importanti</p>
        <p>• La rata non può superare <strong className="text-[#C9A84C]">1/5 (20%)</strong> del netto mensile</p>
        <p>• Durata massima: <strong className="text-[#C9A84C]">120 mesi (10 anni)</strong></p>
        <p>• Il TAN varia da ~6% (PA con cassa previdenza forte) a ~11% (privati piccole aziende)</p>
        <p>• È obbligatoria una polizza vita + rischio impiego inclusa nel costo</p>
        <p>• In caso di perdita del lavoro, la polizza copre le rate residue</p>
      </div>
</motion.div>
  );
};

export default CessioneQuinto;
